/**
 * Local session storage. Holds the opaque session_id we get from
 * POST /auth/exchange. Cleared on logout or when the API returns 401.
 *
 * No PII here — just the session_id, expiry, and a small user shape
 * for showing the user's name/picture in the header.
 */

const KEY = "bht_session";

export type StoredSession = {
  session_id: string;
  expires_at: number; // unix seconds
  user: {
    id: string;
    email: string;
    name: string | null;
    picture_url: string | null;
  };
};

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    // Treat expired sessions as absent.
    if (parsed.expires_at * 1000 < Date.now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(s: StoredSession): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

/**
 * Slide the stored expiry forward. Called when the API returns an
 * X-Session-Expires header (the server re-stamped the session's TTL), so an
 * active user's local session never lapses while the server keeps it alive.
 */
export function extendSession(expires_at: number): void {
  const s = loadSession();
  if (s && expires_at > s.expires_at) saveSession({ ...s, expires_at });
}
